import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Collapse,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { UserAvatar } from '@shared/components/UserAvatar';
import Button from '@shared/components/Buttons/Button';
import {
  RichTextEditor,
  RichTextViewer,
  createEmptyValue,
  stringifySlateValue,
  isContentEmpty,
  parseSlateValue,
  getCharacterCount,
  type CustomElement,
} from '@shared/components/RichTextEditor';
import { UserContext } from '@/shared/contexts/UserContext';
import type { Thread } from '@shared/models/Thread';
import { ThreadContentFormat } from '@shared/models/Thread';
import { useMessageService } from '@shared/services/MessageService';

dayjs.extend(relativeTime);

const MAX_THREAD_LENGTH = 5000;

interface ThreadCardProps {
  thread: Thread;
  onReply: (parentId: number, content: string, isSlateFormat: boolean) => Promise<void>;
  onEdit: (threadId: number, content: string, contentFormat: ThreadContentFormat) => Promise<void>;
  onDelete: (threadId: number) => Promise<void>;
  depth?: number;
  isCreating?: boolean;
  highlightThreadId?: string;
  parentThread?: Thread;
}

const CardContainer = styled(motion.div)<{ $depth: number; $isDeleted?: boolean; $isDeleting?: boolean; $isHighlighted?: boolean }>`
  background: ${({ $depth, $isDeleted, $isDeleting }) => $isDeleted || $isDeleting ? 'rgba(156, 163, 175, 0.05)' : ($depth === 0 ? '#ffffff' : 'rgba(192, 90, 58, 0.02)')};
  border-radius: ${({ $depth }) => $depth === 0 ? '16px' : '12px'};
  border: 1px solid ${({ $depth, $isDeleted, $isDeleting }) => $isDeleted || $isDeleting ? 'rgba(156, 163, 175, 0.15)' : ($depth === 0 ? 'rgba(192, 90, 58, 0.12)' : 'rgba(192, 90, 58, 0.08)')};
  padding: ${({ $depth }) => $depth === 0 ? '20px' : '16px'};
  margin-left: ${({ $depth }) => $depth > 0 ? '24px' : '0'};
  margin-top: ${({ $depth }) => $depth > 0 ? '12px' : '0'};
  position: relative;
  transition: all 0.2s ease;
  opacity: ${({ $isDeleting }) => $isDeleting ? 0.6 : 1};

  &:hover {
    border-color: ${({ $isDeleted, $isDeleting }) => $isDeleted || $isDeleting ? 'rgba(156, 163, 175, 0.2)' : 'rgba(192, 90, 58, 0.25)'};
    box-shadow: ${({ $isDeleted, $isDeleting }) => $isDeleted || $isDeleting ? 'none' : '0 4px 20px rgba(192, 90, 58, 0.08)'};
  }

  ${({ $depth }) => $depth > 0 && `
    &::before {
      content: '';
      position: absolute;
      left: -12px;
      top: 0;
      bottom: 0;
      width: 2px;
      background: linear-gradient(180deg, rgba(192, 90, 58, 0.3) 0%, rgba(192, 90, 58, 0.1) 100%);
      border-radius: 1px;
    }
  `}

  &.thread-attention {
    animation: attentionPulse 2s ease-out;
  }

  @keyframes attentionPulse {
    0% {
      box-shadow: 0 0 0 0px rgba(192, 90, 58, 0.6);
      border-color: rgba(192, 90, 58, 0.8);
      background: rgba(192, 90, 58, 0.08);
    }
    25% {
      box-shadow: 0 0 0 8px rgba(192, 90, 58, 0.3);
      border-color: rgba(192, 90, 58, 0.6);
      background: rgba(192, 90, 58, 0.05);
    }
    50% {
      box-shadow: 0 0 0 4px rgba(192, 90, 58, 0.2);
      border-color: rgba(192, 90, 58, 0.4);
    }
    100% {
      box-shadow: none;
      border-color: rgba(192, 90, 58, 0.12);
      background: ${({ $depth }) => $depth === 0 ? '#ffffff' : 'rgba(192, 90, 58, 0.02)'};
    }
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const AuthorInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const AuthorName = styled(Typography)`
  font-weight: 600;
  font-size: 0.9375rem;
  color: #2a2018;
  line-height: 1.2;
`;

const ReplyCountBadge = styled.span`
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
  margin-left: 8px;
  padding: 2px 8px;
  background: rgba(192, 90, 58, 0.08);
  border-radius: 10px;
`;

const TimeStamp = styled(Typography)`
  font-size: 0.8125rem;
  color: #6b7280;
`;

const ReplyingToText = styled(Typography)`
  font-size: 0.8125rem;
  color: #6b7280;

  strong {
    color: #c05a3a;
    font-weight: 600;
  }
`;

const ContentText = styled(Typography)`
  font-size: 0.9375rem;
  color: #374151;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-word;
`;

const DeletedContent = styled(Typography)`
  font-size: 0.9375rem;
  color: #9ca3af;
  font-style: italic;
  line-height: 1.7;
`;

const ContentWrapper = styled.div`
  font-size: 0.9375rem;
  color: #374151;
  line-height: 1.7;
  word-break: break-word;
`;

const EditorWrapper = styled.div`
  margin-bottom: 12px;
`;

const ActionsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid rgba(192, 90, 58, 0.08);
`;

const ActionButton = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: none;
  border-radius: 8px;
  background: ${({ $active }) => $active ? 'rgba(192, 90, 58, 0.1)' : 'transparent'};
  color: ${({ $active }) => $active ? '#c05a3a' : '#6b7280'};
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(192, 90, 58, 0.08);
    color: #c05a3a;
  }

  svg {
    font-size: 18px;
  }
`;

const ReplyForm = styled(motion.div)`
  margin-top: 16px;
  padding: 16px;
  background: rgba(192, 90, 58, 0.03);
  border-radius: 12px;
  border: 1px solid rgba(192, 90, 58, 0.1);
`;

const ReplyingToLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
  padding: 8px 12px;
  background: rgba(192, 90, 58, 0.08);
  border-radius: 8px;
  font-size: 0.8125rem;
  color: #4b5563;

  strong {
    color: #c05a3a;
    font-weight: 600;
  }
`;

const RepliesContainer = styled.div`
  margin-top: 8px;
`;

const EditedLabel = styled.span`
  font-size: 0.75rem;
  color: #9ca3af;
  margin-left: 8px;
`;

const CharacterCounter = styled.div<{ $isOverLimit?: boolean }>`
  font-size: 0.75rem;
  color: ${({ $isOverLimit }) => ($isOverLimit ? '#ef4444' : '#9ca3af')};
  text-align: right;
  margin-top: 4px;
  font-weight: ${({ $isOverLimit }) => ($isOverLimit ? 600 : 400)};
`;

const ThreadCard: React.FC<ThreadCardProps> = ({
  thread,
  onReply,
  onEdit,
  onDelete,
  depth = 0,
  isCreating = false,
  highlightThreadId,
  parentThread,
}) => {
  const { user: currentUser } = useContext(UserContext);
  const { openMessage } = useMessageService();
  const isHighlighted = highlightThreadId === `thread-${thread.id}`;
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [replyEditorValue, setReplyEditorValue] = useState<CustomElement[]>(createEmptyValue());
  const [editEditorValue, setEditEditorValue] = useState<CustomElement[]>(createEmptyValue());
  const [isDeleting, setIsDeleting] = useState(false);

  const hasHighlightedDescendant = (children: typeof thread.children, targetId: string | undefined): boolean => {
    if (!children || !targetId) return false;
    for (const child of children) {
      if (`thread-${child.id}` === targetId) return true;
      if (hasHighlightedDescendant(child.children, targetId)) return true;
    }
    return false;
  };

  const hasDescendant = hasHighlightedDescendant(thread.children, highlightThreadId);
  // Only auto-expand if this thread or a descendant is highlighted (from URL)
  const shouldAutoExpand = isHighlighted || hasDescendant;

  const [showReplies, setShowReplies] = useState(shouldAutoExpand);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const prevChildrenCount = useRef(thread.children?.length || 0);

  useEffect(() => {
    const currentCount = thread.children?.length || 0;
    if (currentCount > prevChildrenCount.current) {
      setShowReplies(true);
    }
    prevChildrenCount.current = currentCount;
  }, [thread.children?.length, thread.id]);

  useEffect(() => {
    if (highlightThreadId) {
      const isThisHighlighted = `thread-${thread.id}` === highlightThreadId;
      const hasDescendantMatch = hasHighlightedDescendant(thread.children, highlightThreadId);
      if (isThisHighlighted || hasDescendantMatch) {
        setShowReplies(true);
      }
    }
  }, [highlightThreadId, thread.id, thread.children]);

  // Check for slate content - handle both enum value and raw string from API
  // Also detect JSON content if contentFormat is missing (for backwards compatibility)
  const looksLikeJson = thread.content?.startsWith('[{') || thread.content?.startsWith('[{"type"');
  const isSlateContent = thread.contentFormat === ThreadContentFormat.SlateJson
    || (thread.contentFormat as string) === 'slate'
    || (thread.contentFormat === undefined && looksLikeJson);
  const isReplyEmpty = isContentEmpty(replyEditorValue);
  const isEditEmpty = isContentEmpty(editEditorValue);
  const replyCharCount = getCharacterCount(replyEditorValue);
  const editCharCount = getCharacterCount(editEditorValue);
  const isReplyOverLimit = replyCharCount > MAX_THREAD_LENGTH;
  const isEditOverLimit = editCharCount > MAX_THREAD_LENGTH;
  const isDeleted = !!thread.deletedAt;

  const isOwner = currentUser?.id === thread.userId;
  const isAdmin = currentUser?.isAdmin === true;
  const hasReplies = thread.children && thread.children.length > 0;
  const replyCount = thread.children?.length || 0;
  const canEdit = ((isOwner && !hasReplies) || isAdmin) && !isDeleted;
  const canDelete = (isOwner || isAdmin) && !isDeleted;
  // Can reply to your own thread, but not to your own comment (reply)
  const canReply = !isDeleted && (depth === 0 || !isOwner);

  // Check if thread was edited (updatedAt > createdAt by more than a few seconds)
  const wasEdited = dayjs(thread.updatedAt).diff(dayjs(thread.createdAt), 'second') > 5;

  const getDisplayName = (user?: typeof thread.user) => {
    const targetUser = user || thread.user;
    if (isDeleted && !user) return '[deleted]';
    if (targetUser?.username) return targetUser.username;
    if (targetUser?.firstName && targetUser?.lastName) {
      return `${targetUser.firstName} ${targetUser.lastName}`;
    }
    return 'Anonymous';
  };

  const handleStartEdit = () => {
    // Initialize editor with current content
    if (isSlateContent) {
      setEditEditorValue(parseSlateValue(thread.content));
    } else {
      setEditEditorValue([{ type: 'paragraph', children: [{ text: thread.content }] }]);
    }
    setShowEditForm(true);
    setShowReplyForm(false);
  };

  const handleCancelEdit = () => {
    setShowEditForm(false);
    setEditEditorValue(createEmptyValue());
  };

  const handleSubmitEdit = async () => {
    if (isEditEmpty || isEditOverLimit) return;

    setIsSubmitting(true);
    try {
      const content = stringifySlateValue(editEditorValue);
      await onEdit(thread.id, content, ThreadContentFormat.SlateJson);
      setShowEditForm(false);
      setEditEditorValue(createEmptyValue());
    }
    finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitReply = async () => {
    if (isReplyEmpty || isReplyOverLimit) return;

    setIsSubmitting(true);
    try {
      const content = stringifySlateValue(replyEditorValue);
      await onReply(thread.id, content, true);
      setReplyEditorValue(createEmptyValue());
      setShowReplyForm(false);
      setShowReplies(true);
    }
    finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = () => {
    const message = hasReplies
      ? 'This comment has replies. It will be marked as deleted but replies will remain visible.'
      : 'This action cannot be undone.';

    openMessage({
      type: 'warning',
      header: 'Delete Comment?',
      message,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      onConfirm: async () => {
        setIsDeleting(true);
        try {
          await onDelete(thread.id);
        } finally {
          setIsDeleting(false);
        }
      },
    });
  };

  const handleReplyKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      if (!isReplyEmpty && !isSubmitting && !isCreating && !isReplyOverLimit) {
        event.preventDefault();
        handleSubmitReply();
      }
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      setShowReplyForm(false);
      setReplyEditorValue(createEmptyValue());
      return;
    }
  };

  const handleEditKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      if (!isEditEmpty && !isSubmitting && !isEditOverLimit) {
        event.preventDefault();
        handleSubmitEdit();
      }
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      handleCancelEdit();
      return;
    }
  };

  const renderContent = () => {
    if (showEditForm) {
      return (
        <EditorWrapper>
          <RichTextEditor
            value={editEditorValue}
            onChange={(value) => setEditEditorValue(value as CustomElement[])}
            placeholder="Edit your comment..."
            disabled={isSubmitting}
            preset="thread"
            minHeight={80}
            maxHeight={200}
            autoFocus
            onKeyDown={handleEditKeyDown}
          />
          <CharacterCounter $isOverLimit={isEditOverLimit}>
            {editCharCount}/{MAX_THREAD_LENGTH}
          </CharacterCounter>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1.5 }}>
            <Button
              size="small"
              variantType="cancel"
              onClick={handleCancelEdit}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              size="small"
              onClick={handleSubmitEdit}
              disabled={isEditEmpty || isSubmitting || isEditOverLimit}
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </EditorWrapper>
      );
    }

    if (isDeleting) {
      return <DeletedContent>Deleting...</DeletedContent>;
    }

    if (isDeleted) {
      return <DeletedContent>[Comment deleted]</DeletedContent>;
    }

    if (isSlateContent) {
      return (
        <ContentWrapper>
          <RichTextViewer content={thread.content} />
        </ContentWrapper>
      );
    }

    return <ContentText>{thread.content}</ContentText>;
  };

  return (
    <CardContainer
      id={`thread-${thread.id}`}
      $depth={depth}
      $isDeleted={isDeleted}
      $isDeleting={isDeleting}
      $isHighlighted={isHighlighted}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: depth * 0.05 }}
    >
      <Header>
        {!isDeleted && (
          <UserAvatar
            firstName={thread.user?.firstName}
            lastName={thread.user?.lastName}
            username={thread.user?.username}
            avatarColor={thread.user?.avatarColor || '#c05a3a'}
            size={depth === 0 ? 40 : 32}
          />
        )}
        <AuthorInfo>
          <AuthorName style={isDeleted ? { color: '#9ca3af' } : undefined}>
            {getDisplayName()}
            {wasEdited && !isDeleted && <EditedLabel>(edited)</EditedLabel>}
            {depth === 0 && hasReplies && !isDeleted && (
              <ReplyCountBadge>
                {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
              </ReplyCountBadge>
            )}
          </AuthorName>
          {depth > 0 && parentThread && (
            <ReplyingToText>
              Replying to <strong>{getDisplayName(parentThread.user)}</strong>
            </ReplyingToText>
          )}
          <TimeStamp>{dayjs(thread.createdAt).fromNow()}</TimeStamp>
        </AuthorInfo>
      </Header>

      {renderContent()}

      {!showEditForm && (
        <ActionsBar>
          {canReply && (
            <ActionButton
              $active={showReplyForm}
              onClick={() => setShowReplyForm(!showReplyForm)}
            >
              <ChatBubbleOutlineIcon />
              Reply
            </ActionButton>
          )}

          {hasReplies && (
            <ActionButton
              $active={showReplies}
              onClick={() => setShowReplies(!showReplies)}
            >
              {showReplies ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
            </ActionButton>
          )}

          <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
            {canEdit && (
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  onClick={handleStartEdit}
                  sx={{
                    'color': '#9ca3af',
                    '&:hover': {
                      color: '#c05a3a',
                      backgroundColor: 'rgba(192, 90, 58, 0.08)',
                    },
                  }}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {canDelete && (
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={handleDelete}
                  sx={{
                    'color': '#9ca3af',
                    '&:hover': {
                      color: '#ef4444',
                      backgroundColor: 'rgba(239, 68, 68, 0.08)',
                    },
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </ActionsBar>
      )}

      <AnimatePresence>
        {showReplyForm && canReply && (
          <ReplyForm
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <ReplyingToLabel>
              Replying to <strong>{getDisplayName()}</strong>
            </ReplyingToLabel>
            <EditorWrapper>
              <RichTextEditor
                value={replyEditorValue}
                onChange={(value) => setReplyEditorValue(value as CustomElement[])}
                placeholder="Write a reply..."
                disabled={isSubmitting || isCreating}
                preset="thread"
                minHeight={80}
                maxHeight={200}
                onKeyDown={handleReplyKeyDown}
              />
              <CharacterCounter $isOverLimit={isReplyOverLimit}>
                {replyCharCount}/{MAX_THREAD_LENGTH}
              </CharacterCounter>
            </EditorWrapper>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                size="small"
                variantType="cancel"
                onClick={() => {
                  setShowReplyForm(false);
                  setReplyEditorValue(createEmptyValue());
                }}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                size="small"
                onClick={handleSubmitReply}
                disabled={isReplyEmpty || isSubmitting || isCreating || isReplyOverLimit}
              >
                {isSubmitting ? 'Posting...' : 'Post Reply'}
              </Button>
            </Box>
          </ReplyForm>
        )}
      </AnimatePresence>

      <Collapse in={showReplies && hasReplies}>
        <RepliesContainer>
          {thread.children?.map((child) => (
            <ThreadCard
              key={child.id}
              thread={child}
              onReply={onReply}
              onEdit={onEdit}
              onDelete={onDelete}
              depth={depth + 1}
              isCreating={isCreating}
              highlightThreadId={highlightThreadId}
              parentThread={thread}
            />
          ))}
        </RepliesContainer>
      </Collapse>
    </CardContainer>
  );
};

export default ThreadCard;
